import {
  Badge,
  Button,
  Table,
} from '@cloudscape-design/components';
import {
  OrderStatusColorMap,
  OrderTypes,
} from '@parallel-fluidics/shared/constants';
import PropTypes from 'prop-types';
import { useUserData } from 'providers/userDataProvider';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import OrgName from '../Display/orgName';
import UserEmail from '../Display/userEmail';
import { useOrderDetail } from './orderDetailProvider';

function OrderList({
  orders,
  isLoading,
  Pagination = null,
  stickyHeader = false,
  header = null,
  variant = 'container',
}) {
  const { user: { canEditOrder } } = useUserData();
  const { setOrderId } = useOrderDetail();
  const navigate = useNavigate();

  const orderRows = useMemo(() => orders.map((order) => {
    const targetShipsArr = [];
    const projectIdSet = new Set();
    order.shipments.forEach((shipment) => {
      targetShipsArr.push(shipment.targetShipDate);
      shipment.lineItems.forEach((lineItem) => {
        const projectLabel = [lineItem.projectId, lineItem.pPartId].filter(Boolean).join('-');
        projectIdSet.add(projectLabel);
      });
    });
    const targetShips = targetShipsArr.join(', ');
    const projectIds = Array.from(projectIdSet).join(', ');
    return {
      ...order,
      targetShips: <span title={targetShips}>{targetShips}</span>,
      projectIds: <span title={projectIds}>{projectIds}</span>,
      orgName: <OrgName orgId={order.organization} clickable={variant === 'full-page'} />,
      userEmail: <UserEmail userId={order.uid} clickable={variant === 'full-page'} />,
      nameButton: (
        <Button
          fullWidth
          variant="inline-link"
          onClick={() => setOrderId(order.id)}
        >
          <div
            className="truncate"
            style={{ textAlign: 'left' }}
            title={order.name || 'MISSING'}
            aria-label={order.name || 'MISSING'}
          >
            {order.name || 'MISSING'}
          </div>
        </Button>
      ),
      statusBadge: (
        <Badge
          color={OrderStatusColorMap[order.status] || 'grey'}
        >
          {order.status}
        </Badge>
      ),
    };
  }), [orders, setOrderId, variant]);

  return (
    <Table
      header={header}
      variant={variant}
      loading={isLoading}
      items={orderRows}
      stickyHeader={variant === 'full-page' || stickyHeader}
      resizableColumns={variant === 'full-page'}
      columnDefinitions={[
        {
          id: 'name',
          header: 'Order ID',
          cell: (item) => item.nameButton,
          maxWidth: 120,
        },
        {
          id: 'projectIds',
          header: 'Projects',
          cell: (item) => item.projectIds,
          maxWidth: 120,
        },
        {
          id: 'orderDate',
          header: 'Order Date',
          cell: (item) => item.orderDate,
        },
        {
          id: 'targetShips',
          header: 'Target Ship Dates',
          cell: (item) => item.targetShips,
          maxWidth: 120,
        },
        {
          id: 'organization',
          header: 'Organization',
          cell: (item) => item.orgName,
          maxWidth: 120,
        },
        {
          id: 'userId',
          header: 'Owner',
          cell: (item) => item.userEmail,
          maxWidth: 120,
        },
        {
          id: 'status',
          header: 'Status',
          cell: (item) => item.statusBadge,
          minWidth: 120,
        },
        ...((variant === 'full-page' && canEditOrder) ? [
          {
            id: 'actions',
            header: 'Actions',
            // eslint-disable-next-line react/no-unstable-nested-components
            cell: (item) => <Button onClick={() => navigate(`/orders/update/${item.id}?type=${item.isInternal ? OrderTypes.INTERNAL : OrderTypes.CUSTOMER}`)}>Edit</Button>,
          },
        ] : []),
      ]}
      pagination={Pagination}
      empty={<div>No orders</div>}
    />
  );
}

OrderList.propTypes = {
  header: PropTypes.node,
  variant: PropTypes.string,
  orders: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    status: PropTypes.string,
    shipments: PropTypes.arrayOf(PropTypes.shape({
      targetShipDate: PropTypes.string,
      lineItems: PropTypes.arrayOf(PropTypes.shape({
        projectId: PropTypes.string,
      })),
    })),
    organization: PropTypes.string,
    orderDate: PropTypes.string,
  })).isRequired,
  isLoading: PropTypes.bool.isRequired,
  Pagination: PropTypes.node,
  stickyHeader: PropTypes.bool,
};

export default OrderList;
